/**
 * driveTokenStore (issue #30 Phase 1)
 *
 * Google Drive OAuth access token を scope ごとに sessionStorage で管理する singleton。
 * 全 hook (useGoogleDrivePicker / useGoogleDriveSaver) は本 module 経由で token を取得し、
 * 「毎回 OAuth ダイアログ」問題 (spec §3 root cause) を解消する。
 *
 * 機能:
 * - sessionStorage 永続化 (タブ閉じで消える、 セッション中は再利用)
 * - expires_in 期限管理 (60 秒のマージンで早めに refresh)
 * - drive.file 単一 scope (issue #123: drive.readonly 廃止で CASA 回避・一般公開)
 *
 * Phase 3 で silent refresh (prompt: 'none') + consent フォールバックを追加する。
 *
 * spec: docs/superpowers/specs/2026-05-24-google-drive-auth-frequency-analysis.md §4
 */

// issue #123: drive.readonly (restricted・CASA 必須) を全廃し drive.file 単一に統一。
export type DriveScope = 'file';

/** sessionStorage キーの prefix (issue #29 命名規約)。 */
export const DRIVE_TOKEN_KEY_PREFIX = 'omakase-unshi:drive-token:';

/** scope の論理名 → Google API URL のマップ。 */
const SCOPE_URLS: Record<DriveScope, string> = {
  file: 'https://www.googleapis.com/auth/drive.file',
};

interface CachedToken {
  accessToken: string;
  expiresAt: number;
  scope: string;
}

/** expires まで何ミリ秒残っていれば cache hit 扱いか。 60 秒。 */
const REFRESH_MARGIN_MS = 60_000;

/**
 * token 変化 (保存 / クリア) を購読する listener 群 (issue #35 課題2-B)。
 * scope 別 token + hook インスタンス別 isLoggedIn の二重分裂を解消し、
 * driveTokenStore を single source of truth にする。 各 hook は subscribeDriveToken で購読し、
 * useSyncExternalStore 等で 4 インスタンスのログイン状態を自動同期する。
 */
const tokenListeners = new Set<() => void>();

function notifyTokenListeners(): void {
  tokenListeners.forEach((listener) => listener());
}

function storageKey(scope: DriveScope): string {
  return `${DRIVE_TOKEN_KEY_PREFIX}${scope}`;
}

function loadCached(scope: DriveScope): CachedToken | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.accessToken === 'string' &&
      typeof parsed?.expiresAt === 'number' &&
      typeof parsed?.scope === 'string'
    ) {
      return parsed as CachedToken;
    }
    return null;
  } catch {
    return null;
  }
}

function saveCached(scope: DriveScope, cached: CachedToken): void {
  try {
    window.sessionStorage.setItem(storageKey(scope), JSON.stringify(cached));
  } catch (e) {
    console.warn('[driveTokenStore] failed to save token cache', e);
  }
  // token 変化を購読者へ通知 (issue #35 課題2-B、 ログイン状態集約)。
  notifyTokenListeners();
}

function isFresh(cached: CachedToken | null): cached is CachedToken {
  if (!cached) return false;
  return cached.expiresAt - Date.now() > REFRESH_MARGIN_MS;
}

interface TokenResponseLike {
  accessToken: string;
  expiresIn: number;
  scope: string;
}

/**
 * GIS にトークンをリクエストする。
 * prompt: 'none' = silent (UI なし、 既存 Google セッションから払い出し)
 * prompt: 'consent' = ダイアログ表示
 */
function requestAccessToken(
  scope: DriveScope,
  prompt: '' | 'none' | 'consent',
): Promise<TokenResponseLike> {
  return new Promise((resolve, reject) => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      reject(new Error('REACT_APP_GOOGLE_CLIENT_ID is not set'));
      return;
    }
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE_URLS[scope],
      callback: (resp) => {
        if (resp.error) {
          reject(new Error(resp.error));
          return;
        }
        resolve({
          accessToken: resp.access_token,
          expiresIn: resp.expires_in,
          scope: resp.scope ?? SCOPE_URLS[scope],
        });
      },
      // popup_closed / popup_failed_to_open / silent 失敗は callback ではなくここに来る。
      // 未配線だと Promise が永久 pending になり consent fallback に到達しない (issue #35 P0)。
      error_callback: (error) => {
        reject(new Error(error.type ?? 'token request failed'));
      },
    });
    client.requestAccessToken({ prompt });
  });
}

/**
 * silent (prompt: 'none') で取得を試み、 失敗時のみ consent ダイアログを出す。
 * 既存 Google セッションがあれば silent で済むため UX フリクション最小。
 */
async function requestAccessTokenWithFallback(scope: DriveScope): Promise<TokenResponseLike> {
  try {
    return await requestAccessToken(scope, 'none');
  } catch {
    return await requestAccessToken(scope, 'consent');
  }
}

export interface GetDriveTokenOptions {
  /**
   * ユーザー gesture (click) 同期直下で呼ぶ経路では true にする (issue #35 P0 #57)。
   * cache miss 時に silent (prompt:'none') を skip し、await を挟まず consent 直行する。
   * silent の await を挟むと user-activation が切れ、後続 consent popup がモバイルでブロックされる。
   * 既定 (false) は従来の silent-first (background 互換)。
   */
  interactive?: boolean;
}

/**
 * scope に応じた token を取得する。 cache hit / cache miss を自動判定。
 * - cache が新鮮 (60 秒以上残) → cache 値を返す (popup を出さない)
 * - cache が古い / 不在:
 *   - interactive=false (既定): silent-first (silent → 失敗時 consent)
 *   - interactive=true: silent を skip して consent 直行 (popup blocker 回避、issue #35 P0)
 */
export async function getDriveToken(
  scope: DriveScope,
  opts: GetDriveTokenOptions = {},
): Promise<string> {
  // loadCached は同期。 cache miss → requestAccessToken('consent') の間に await を挟まないことで
  // user-activation を保持する (interactive 経路の popup blocker 回避、issue #35 P0)。
  const cached = loadCached(scope);
  if (isFresh(cached)) {
    return cached.accessToken;
  }

  const resp = opts.interactive
    ? await requestAccessToken(scope, 'consent')
    : await requestAccessTokenWithFallback(scope);
  const next: CachedToken = {
    accessToken: resp.accessToken,
    expiresAt: Date.now() + resp.expiresIn * 1000,
    scope: resp.scope,
  };
  saveCached(scope, next);
  return next.accessToken;
}

/**
 * 明示的な再ログイン用 (issue #35 M-2)。silent を試さず即 consent ダイアログを出す。
 * 「再ログイン」ボタンの click ハンドラ同期直下で呼ぶこと (popup blocker 回避、spec §3.2)。
 * 実体は getDriveToken の interactive 経路 (issue #35 P0 #57 で統一)。
 * ただし cache fresh でも必ず consent を出す「強制再ログイン」セマンティクスを保つため cache を捨てる。
 */
export async function getDriveTokenInteractive(scope: DriveScope): Promise<string> {
  clearDriveToken(scope);
  return getDriveToken(scope, { interactive: true });
}

/**
 * drive.file token を取得する「ログイン」経路 (issue #35 課題2-A → issue #123 で file 単一化)。
 * signInInteractive / getSilentToken から呼ばれる。 実体は getDriveToken('file') と同じ file token だが、
 * ログイン導線としての意味付けで別名を残す (DriveAuthContext の consumer を無改変に保つ)。
 * - cache: file が fresh なら consent を出さず即返す。
 * - interactive=true は #57 同様 silent (prompt:'none') を skip し consent 直行 (gesture 保持)。
 */
export async function getCombinedDriveToken(
  opts: GetDriveTokenOptions = {},
): Promise<string> {
  const cachedFile = loadCached('file');
  if (isFresh(cachedFile)) {
    return cachedFile.accessToken;
  }

  const resp = opts.interactive
    ? await requestAccessToken('file', 'consent')
    : await requestAccessTokenWithFallback('file');

  const next: CachedToken = {
    accessToken: resp.accessToken,
    expiresAt: Date.now() + resp.expiresIn * 1000,
    scope: resp.scope,
  };
  saveCached('file', next);
  return next.accessToken;
}

/**
 * token 変化 (保存 / クリア) を購読する (issue #35 課題2-B)。
 * listener は saveCached / clearDriveToken の度に呼ばれる。 返り値で unsubscribe できる。
 * hook 側は useSyncExternalStore 等で購読し、 4 インスタンスのログイン状態を自動同期する。
 */
export function subscribeDriveToken(listener: () => void): () => void {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
}

/**
 * drive.file の有効 (fresh) な token が cache されているか (issue #35 M-3a / #123 file 単一)。
 * ログイン状態 indicator (boolean) の判定に使う。userinfo は取得しない。
 */
export function hasValidDriveToken(): boolean {
  return isFresh(loadCached('file'));
}

/**
 * drive.file token が fresh か (issue #35 課題2-B / issue #123 で file 単一化)。
 * ログイン後はこれが true。 indicator を「🟢 = drive.file 取得済 = 全機能使える」と倒すための判定。
 * (readonly 廃止に伴い hasValidDriveToken と同値だが、 indicator 用途の別名として残置。)
 */
export function hasFullDriveAccess(): boolean {
  return isFresh(loadCached('file'));
}

/**
 * fresh な cache token を同期で読む (issue #35 課題1、 PR2)。 fresh が無ければ null。
 * openPicker が consent を await せず (gesture 保持) cache 済 token で setVisible するために使う。
 * popup や silent refresh は一切起こさない純粋 reader。
 */
export function peekDriveToken(scope: DriveScope): string | null {
  const cached = loadCached(scope);
  return isFresh(cached) ? cached.accessToken : null;
}

/**
 * token cache をクリアする。 引数なしなら全 scope を削除。
 * 401 受信時 / ログアウト時に呼ぶ想定。
 */
export function clearDriveToken(scope?: DriveScope): void {
  try {
    // scope 指定/未指定いずれも file を掃除 (issue #123: readonly 廃止で単一 scope)。
    window.sessionStorage.removeItem(storageKey(scope ?? 'file'));
  } catch (e) {
    console.warn('[driveTokenStore] failed to clear token cache', e);
  }
  // token 変化を購読者へ通知 (issue #35 課題2-B、 ログアウトで全 hook の状態を false 化)。
  notifyTokenListeners();
}
